import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Card,
  CardContent,
  Grid,
  FormControlLabel,
  Switch,
  CircularProgress, // Added for a better loading indicator
} from "@mui/material";
import axios from "axios";
import config from "../config";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";

const baseUrl = config.baseURL;
const steps = ["Module Info", "Add Fields", "Permissions"];

// Define the primary and accent colors for consistency
const PRIMARY_COLOR = "#2E4053"; // Dark Blue
const ACCENT_COLOR = "#2E4053"; // brown #8B4513

const ModuleCreate = () => {
  const [token] = useState(localStorage.getItem("token"));
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const [moduleData, setModuleData] = useState({
    slug: "",
    name: "",
    description: "",
    is_active: true,
  });

  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode(token);
        setUserData(decoded);
      } catch (e) {
        console.error("Invalid token");
        // Optionally, handle invalid token (e.g., redirect to login)
      }
    }
  }, [token]);

  const handleCreateModule = async () => {
    // Basic validation
    if (!moduleData.name.trim()) {
      alert("Please provide a display name for the module.");
      return;
    }

    console.log("moduleData", moduleData);
    setLoading(true);
    try {
      const res = await axios.post(`${baseUrl}/api/module`, moduleData, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 180000,
      });
      console.log("res", res);
      const id = res.data.id;
      // Use a custom alert or Snackbar in a real app
      alert("Module created successfully!");

      // Navigate to Step 2: Add Fields
      navigate(`/dashboard/modules/fields/${id}`);
    } catch (err) {
      console.error(
        "Error creating module:",
        err.response ? err.response.data : err.message
      );
      // Display a more informative error
      alert(
        `Failed to create module: ${
          err.response?.data?.message || "Server error"
        }`
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    // Set a consistent background/padding for the overall page content
    <Box
      sx={{
        p: { xs: 2, sm: 4 }, // Responsive padding
        minHeight: "100vh",
        backgroundColor: "#F4F6F8", // A light, clean background
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: "600",
          mb: 4,
          color: PRIMARY_COLOR, // Use primary color for the title
        }}
      >
        ➕ Create New Module
      </Typography>

      {/* Stepper Styling */}
      <Stepper
        activeStep={0}
        sx={{
          mb: 5,
          // Customize the step color (dots/lines)
          "& .MuiStepIcon-root.Mui-active": {
            color: PRIMARY_COLOR,
          },
          "& .MuiStepIcon-root.Mui-completed": {
            color: PRIMARY_COLOR,
          },
          "& .MuiStepLabel-label": {
            fontWeight: 500,
            color: "#34495E",
          },
        }}
      >
        {steps.map((label) => (
          <Step key={label}>
            <StepLabel>{label}</StepLabel>
          </Step>
        ))}
      </Stepper>

      {/* Card Styling */}
      <Card
        sx={{
          borderRadius: "12px",
          boxShadow: "0 4px 12px rgba(0, 0, 0, 0.05)", // Subtle shadow
        }}
      >
        <CardContent>
          <Grid container spacing={3} direction="column">
            {/* Display Name */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Display Name"
                variant="outlined" // Explicitly use outlined variant
                value={moduleData.name}
                onChange={(e) => {
                  const name = e.target.value;
                  const slug = name
                    .toLowerCase()
                    .trim()
                    .replace(/\s+/g, "-")
                    .replace(/[^a-z0-9-]/g, ""); // auto-generate slug
                  setModuleData({ ...moduleData, name, slug });
                }}
                // Apply color to the focused state
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: PRIMARY_COLOR,
                    },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: PRIMARY_COLOR,
                  },
                }}
              />
            </Grid>

            {/* Module Slug (auto-filled, disabled) */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Module Slug"
                variant="outlined"
                value={moduleData.slug}
                disabled
                // Style for disabled/read-only input
                sx={{
                  "& .MuiInputBase-input.Mui-disabled": {
                    backgroundColor: "#F0F0F0", // Light gray background
                    color: "#757575", // Darker text for readability
                  },
                }}
              />
            </Grid>

            {/* Description */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Description"
                variant="outlined"
                multiline
                rows={3}
                value={moduleData.description}
                onChange={(e) =>
                  setModuleData({
                    ...moduleData,
                    description: e.target.value,
                  })
                }
                sx={{
                  "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline":
                    {
                      borderColor: PRIMARY_COLOR,
                    },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: PRIMARY_COLOR,
                  },
                }}
              />
            </Grid>

            {/* Active Toggle */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={moduleData.is_active}
                    onChange={(e) =>
                      setModuleData({
                        ...moduleData,
                        is_active: e.target.checked,
                      })
                    }
                    // Style the switch with the accent color
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: ACCENT_COLOR,
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: ACCENT_COLOR,
                        },
                    }}
                  />
                }
                label={
                  <Typography sx={{ color: PRIMARY_COLOR, fontWeight: 500 }}>
                    Active
                  </Typography>
                }
              />
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {/* Action Button */}
      <Box sx={{ mt: 5 }}>
        <Button
          variant="contained"
          onClick={handleCreateModule}
          disabled={loading}
          sx={{
            backgroundColor: ACCENT_COLOR, // Orange accent color
            color: "#FFFFFF", // White text
            padding: "12px 24px",
            fontSize: "16px",
            fontWeight: "bold",
            borderRadius: "8px",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#c7570f", // Darker orange for hover
              boxShadow: "0px 6px 15px rgba(234, 106, 18, 0.3)", // Orange shadow
            },
            minWidth: "200px",
          }}
        >
          {loading ? (
            <CircularProgress size={24} sx={{ color: "#FFFFFF" }} />
          ) : (
            "Create & Continue →"
          )}
        </Button>
      </Box>
    </Box>
  );
};

export default ModuleCreate;
